import { Firestore } from "@google-cloud/firestore";

import {
  ClassroomAttachmentEntity,
  classroomAttachmentEntitySchema,
  ClassroomCourseWorkEntity,
  classroomCourseWorkEntitySchema,
  CourseWorkPupilProgress,
  courseWorkPupilProgressSchema,
  UserCredentialsEntity,
  userCredentialsEntitySchema,
  PupilLessonProgress,
  pupilLessonProgressSchema,
} from "@/types/services";
import { handleFirestoreError } from "@/errors/services";
import type { ErrorContext } from "@/errors";
import { ExceptionType } from "@/errors";

enum FirestoreCollections {
  CONNECTED_USERS_CREDENTIALS = "connectedUsersCredentials",
  CLASSROOM_ATTACHMENTS = "classroomAttachments",
  PUPIL_LESSON_PROGRESS = "pupilLessonProgress",
  CLASSROOM_COURSEWORK = "classroomCourseWork",
  COURSEWORK_PUPIL_PROGRESS = "courseWorkPupilProgress",
}

export class FirestoreClient {
  private _firestore: Firestore;

  constructor(private readonly firestore: Firestore) {
    this._firestore = firestore;
    this._firestore.settings({ ignoreUndefinedProperties: true });
  }

  public async getConnectedUserCredentials(
    loginHint: string,
  ): Promise<UserCredentialsEntity | null> {
    try {
      const doc = await this._firestore
        .collection(FirestoreCollections.CONNECTED_USERS_CREDENTIALS)
        .doc(loginHint)
        .get();

      if (!doc.exists) {
        return null;
      }
      const parsedDoc = userCredentialsEntitySchema.parse(doc.data());
      return parsedDoc;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getConnectedUserCredentials",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to get users credentials",
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async upsertConnectedUserCredentials(
    credentials: Omit<UserCredentialsEntity, "refreshToken"> & {
      refreshToken?: string;
    },
  ): Promise<void> {
    try {
      const parsedCredentials = userCredentialsEntitySchema.parse(credentials);
      // Strip undefined fields so merge doesn't overwrite existing Firestore fields.
      // Zod includes optional keys with value `undefined` in its output, and Firestore
      // merge + undefined values will delete the existing field.
      const definedCredentials = Object.fromEntries(
        Object.entries(parsedCredentials).filter(([, v]) => v !== undefined),
      );
      await this._firestore
        .collection(FirestoreCollections.CONNECTED_USERS_CREDENTIALS)
        .doc(parsedCredentials.loginHint)
        .set(definedCredentials, { merge: true });
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "upsertConnectedUserCredentials",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to upsert users credentials",
          originalError: error,
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async upsertClassroomAttachment(
    data: ClassroomAttachmentEntity,
  ): Promise<void> {
    try {
      const parsedData = classroomAttachmentEntitySchema.parse(data);
      await this._firestore
        .collection(FirestoreCollections.CLASSROOM_ATTACHMENTS)
        .doc(parsedData.attachmentId)
        .set(parsedData);
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "upsertClassroomAttachment",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to upsert classroom attachment",
          originalError: error,
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async getClassroomAttachmentById(
    attachmentId: string,
  ): Promise<ClassroomAttachmentEntity | null> {
    try {
      const querySnapshot = await this._firestore
        .collection(FirestoreCollections.CLASSROOM_ATTACHMENTS)
        .doc(attachmentId)
        .get();

      if (!querySnapshot.exists) {
        return null;
      }

      // Assuming attachmentId is unique, we take the first matching document
      // const doc = querySnapshot;
      const parsedSnapshot = classroomAttachmentEntitySchema.parse(
        querySnapshot.data(),
      );
      return parsedSnapshot;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getClassroomAttachmentById",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to get classroom attachment by ID",
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async upsertPupilLessonProgress(
    data: PupilLessonProgress,
  ): Promise<void> {
    try {
      const parsedData = pupilLessonProgressSchema.parse(data);
      const compositeKey = `${parsedData.submissionId}:${parsedData.attachmentId}:${parsedData.itemId}`;
      await this._firestore
        .collection(FirestoreCollections.PUPIL_LESSON_PROGRESS)
        .doc(compositeKey)
        .set(parsedData, { merge: true });
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "upsertPupilLessonProgress",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to upsert pupil lesson progress",
          originalError: error,
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async getPupilLessonProgress(
    submissionId: string,
    attachmentId: string,
    itemId: string,
  ): Promise<PupilLessonProgress | null> {
    try {
      const compositeKey = `${submissionId}:${attachmentId}:${itemId}`;
      const doc = await this._firestore
        .collection(FirestoreCollections.PUPIL_LESSON_PROGRESS)
        .doc(compositeKey)
        .get();

      if (!doc.exists) {
        return null;
      }
      return pupilLessonProgressSchema.parse(doc.data());
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getPupilLessonProgress",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to get pupil lesson progress",
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async saveClassroomCourseWork(
    assignmentToken: string,
    entity: ClassroomCourseWorkEntity,
  ): Promise<void> {
    try {
      const parsed = classroomCourseWorkEntitySchema.parse(entity);
      await this._firestore
        .collection(FirestoreCollections.CLASSROOM_COURSEWORK)
        .doc(assignmentToken)
        .set(parsed);
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "saveClassroomCourseWork",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to save classroom coursework",
          originalError: error,
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async getClassroomCourseWork(
    assignmentToken: string,
  ): Promise<ClassroomCourseWorkEntity | null> {
    try {
      const doc = await this._firestore
        .collection(FirestoreCollections.CLASSROOM_COURSEWORK)
        .doc(assignmentToken)
        .get();

      if (!doc.exists) {
        return null;
      }
      return classroomCourseWorkEntitySchema.parse(doc.data());
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getClassroomCourseWork",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to get classroom coursework",
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async upsertCourseWorkPupilProgress(
    data: CourseWorkPupilProgress,
  ): Promise<void> {
    try {
      const parsed = courseWorkPupilProgressSchema.parse(data);
      const compositeKey = `${parsed.submissionId}:${parsed.assignmentToken}`;
      await this._firestore
        .collection(FirestoreCollections.COURSEWORK_PUPIL_PROGRESS)
        .doc(compositeKey)
        .set(parsed, { merge: true });
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "upsertCourseWorkPupilProgress",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to upsert coursework pupil progress",
          originalError: error,
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }

  public async getCourseWorkPupilProgress(
    submissionId: string,
    assignmentToken: string,
  ): Promise<CourseWorkPupilProgress | null> {
    try {
      const compositeKey = `${submissionId}:${assignmentToken}`;
      const doc = await this._firestore
        .collection(FirestoreCollections.COURSEWORK_PUPIL_PROGRESS)
        .doc(compositeKey)
        .get();

      if (!doc.exists) {
        return null;
      }
      return courseWorkPupilProgressSchema.parse(doc.data());
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getCourseWorkPupilProgress",
        service: ExceptionType.Firestore,
        metadata: {
          message: "Failed to get coursework pupil progress",
        },
      };
      handleFirestoreError(error, errorContext);
    }
  }
}
